import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MathUtils } from 'three';
import { Box, Stack } from '@mui/system';

import { NumUtils } from '@/modules/common/utils/NumUtils';
import { KnobInput } from '@/modules/common/components/inputs/components/KnobInput';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';

const ANGLE_MIN = 0;
const ANGLE_MAX = 360;

const toCommitRadians = (angleDeg: number): number => {
  const normalizedDeg = NumUtils.restrictToCircularRange(angleDeg, 360);
  return MathUtils.degToRad(normalizedDeg);
};

type AngleProps = {
  /** null = different angles in the selection: input shows 'Mixed', knob is disabled */
  valueRad: number | null;
  onCommit: (valueRad: number) => void;
  onPreview?: (valueRad: number) => void;
  min?: number;
  max?: number;
  parentRotationRad?: number;
  decimals?: number;
};

const AngleComponent = ({
  valueRad,
  onCommit,
  onPreview,
  min = ANGLE_MIN,
  max = ANGLE_MAX,
  parentRotationRad,
  decimals = 0,
}: AngleProps) => {
  const isMixed = valueRad === null;
  const initialAngleDeg = valueRad === null ? 0 : MathUtils.radToDeg(valueRad);
  const [localAngleDeg, setLocalAngleDeg] = useState(initialAngleDeg);

  const localAngleDegRef = useRef(initialAngleDeg);
  const lastCommittedRadRef = useRef(toCommitRadians(initialAngleDeg));
  const hasKnobPreviewRef = useRef(false);

  useEffect(() => {
    if (valueRad === null) return;

    const nextAngleDeg = MathUtils.radToDeg(valueRad);

    localAngleDegRef.current = nextAngleDeg;
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocalAngleDeg(nextAngleDeg);

    if (!hasKnobPreviewRef.current) {
      lastCommittedRadRef.current = toCommitRadians(nextAngleDeg);
    }
  }, [valueRad]);

  const parentRotationDeg = useMemo(() => {
    if (!parentRotationRad || parentRotationRad === null) return 0;
    return MathUtils.radToDeg(parentRotationRad);
  }, [parentRotationRad]);

  const offsetDeg = useMemo(() => {
    const angle = 90 - parentRotationDeg;
    return NumUtils.restrictToCircularRange(angle, 360);
  }, [parentRotationDeg]);

  const commitAngle = useCallback(
    (angleDeg: number) => {
      const clamped = MathUtils.clamp(angleDeg, min, max);
      const nextRad = toCommitRadians(clamped);

      hasKnobPreviewRef.current = false;
      localAngleDegRef.current = clamped;
      setLocalAngleDeg((current) => (current === clamped ? current : clamped));

      if (nextRad === lastCommittedRadRef.current) return;

      lastCommittedRadRef.current = nextRad;
      onCommit(nextRad);
    },
    [min, max, onCommit],
  );

  const onKnobCommit = useCallback(() => commitAngle(localAngleDegRef.current), [commitAngle]);

  const onInputChange = useCallback(
    (newValue: number) => {
      if (Number.isNaN(newValue)) return;

      const clamped = MathUtils.clamp(newValue, min, max);
      if (clamped === localAngleDegRef.current) return;

      localAngleDegRef.current = clamped;
      setLocalAngleDeg(clamped);
    },
    [min, max],
  );

  const onKnobChange = useCallback(
    (newValueDeg: number) => {
      const clamped = MathUtils.clamp(newValueDeg, min, max);
      const previousRad = toCommitRadians(localAngleDegRef.current);
      const nextRad = toCommitRadians(clamped);

      if (clamped === localAngleDegRef.current) return;

      localAngleDegRef.current = clamped;
      setLocalAngleDeg(clamped);

      if (nextRad !== previousRad) {
        hasKnobPreviewRef.current = true;
        onPreview?.(nextRad);
      }
    },
    [min, max, onPreview],
  );

  return (
    <PropertiesLabel i18nkey='interface:properties.area.direction'>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Box flex={1} justifyContent='flex-start' display='flex'>
          <KnobInput
            valueDeg={localAngleDeg}
            onChange={onKnobChange}
            onCommit={onKnobCommit}
            min={min}
            max={max}
            offsetDeg={offsetDeg}
            decimals={decimals}
            invertVisual
            disabled={isMixed || undefined}
          />
        </Box>

        <Box flex={2}>
          <NumberInput
            minValue={min}
            maxValue={max}
            value={isMixed ? null : localAngleDeg}
            onChange={onInputChange}
            onBlur={commitAngle}
            debounceMs={0}
            useButtons
            decimals={decimals}
          />
        </Box>
      </Stack>
    </PropertiesLabel>
  );
};

export const Angle = memo(AngleComponent);
